import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { FormFieldInput } from '@/shared/components/custom/form-field/FormFieldInput';
import { FormFieldAsyncCombobox } from '@/shared/components/custom/form-field/FormFieldAsyncCombobox';
import { FormFieldDatePicker } from '@/shared/components/custom/form-field/FormFieldDatePicker';
import { useInviteMember } from '../hooks/useInviteMember';
import { useToast } from '@/shared/hooks/useToast';
import { createInviteMemberSchema, type InviteFormValues } from '../schemas';
import { MEMBER_ROLES, MemberRole } from '../types';

interface InviteMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InviteMemberDialog({ open, onOpenChange }: InviteMemberDialogProps) {
  const { t } = useTranslation('members');
  const inviteSchema = createInviteMemberSchema(t);
  const inviteMember = useInviteMember();
  const { toast } = useToast();

  const { control, handleSubmit, reset } = useForm<InviteFormValues>({
    resolver: zodResolver(inviteSchema),
    defaultValues: {
      email: '',
      role: MemberRole.Collaborator,
      expiresAt: null,
    },
  });

  const roleOptions = MEMBER_ROLES.map((role) => ({
    id: role,
    label: t(`role.${role}`),
  }));

  const onSubmit = async (data: InviteFormValues) => {
    try {
      await inviteMember.mutateAsync({
        email: data.email,
        role: data.role,
        expiresAt: data.expiresAt ?? undefined,
      });

      toast.success({
        title: t('toast.inviteSent'),
        description: t('toast.inviteSentDesc', { email: data.email }),
      });

      reset();
      onOpenChange(false);
    } catch {
      // global handler shows error toast
    }
  };

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      reset();
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="w-full max-w-none rounded-none md:max-w-105 md:rounded-lg">
        <DialogHeader>
          <DialogTitle>{t('invite.title')}</DialogTitle>
          <DialogDescription>{t('invite.description')}</DialogDescription>
        </DialogHeader>

        <form
          id="invite-form"
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-6 py-2"
        >
          <FormFieldInput
            control={control}
            name="email"
            label={t('invite.email')}
            type="email"
            floatingLabel
            data-testid="members-invite-email-input"
          />

          <FormFieldAsyncCombobox
            control={control}
            name="role"
            label={t('invite.role')}
            queryKey={['member-roles-static']}
            staticOptions={roleOptions}
            floatingLabel
            clearable={false}
            data-testid="members-invite-role-select"
          />

          <FormFieldDatePicker
            control={control}
            name="expiresAt"
            label={t('invite.expiration')}
            minDate={new Date()}
            floatingLabel
            data-testid="members-invite-expiration-picker"
          />
        </form>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => handleClose(false)}
            data-testid="members-invite-cancel-button"
          >
            {t('invite.cancel')}
          </Button>
          <Button
            type="submit"
            form="invite-form"
            disabled={inviteMember.isPending}
            data-testid="members-invite-submit-button"
          >
            {inviteMember.isPending ? t('invite.submitting') : t('invite.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
